import { createContext, useContext, useState, type ReactNode } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

/**
 * Set to `true` only to design the banner + sidebar entry. Normally `false` so
 * they surface only when a new build's service worker is actually waiting.
 */
const FORCE_SHOW = false

interface UpdateContextValue {
  /** A newer build is waiting (or forced on for design). */
  updateAvailable: boolean
  /** Whether the centre-screen banner has been dismissed via "Later". */
  bannerDismissed: boolean
  /** Hide the banner — the sidebar entry stays so the update isn't lost. */
  dismissBanner: () => void
  /** Activate the waiting worker and reload onto the new build. */
  applyUpdate: () => void
  /** True between clicking refresh and the reload. */
  applying: boolean
}

const UpdateContext = createContext<UpdateContextValue | null>(null)

/**
 * Owns the single `useRegisterSW` registration and shares "is an update
 * waiting / apply it" with both the centre-screen banner and the persistent
 * sidebar entry — so dismissing the banner ("Later") still leaves a way back in.
 */
export function UpdateProvider({ children }: { children: ReactNode }) {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, r) {
      console.log('sw-registered', { swUrl, registration: r })
    },
    onRegisterError(error) {
      console.error('SW registration error:', error)
    },
  })

  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [applying, setApplying] = useState(false)

  const value: UpdateContextValue = {
    updateAvailable: FORCE_SHOW || needRefresh,
    bannerDismissed,
    dismissBanner: () => {
      setBannerDismissed(true)
    },
    applying,
    applyUpdate: () => {
      setApplying(true)
      void updateServiceWorker(true)
    },
  }

  return (
    <UpdateContext.Provider value={value}>{children}</UpdateContext.Provider>
  )
}

export function useUpdateAvailable(): UpdateContextValue {
  return (
    useContext(UpdateContext) ?? {
      updateAvailable: false,
      bannerDismissed: true,
      dismissBanner: () => {},
      applyUpdate: () => {},
      applying: false,
    }
  )
}
